'use client';

import { Badge, Button } from '@/components/ui';
import { cn } from '@/libs';
import { formatCurrency, ticketLabels, type PromotionOffer } from '@/utils';

type UpsellOfferCardProps = {
  offer: PromotionOffer;
  price: number;
  fullPrice: number;
  onAccept: () => void;
};

export const UpsellOfferCard = ({
  offer,
  price,
  fullPrice,
  onAccept,
}: UpsellOfferCardProps) => {
  const hasDiscount = offer.percentage > 0;
  const quantityLabel =
    offer.quantity === 1
      ? `1 ${ticketLabels.singularCaps}`
      : `${offer.quantity} ${ticketLabels.pluralCaps}`;

  return (
    <div className="relative w-full rounded-xl border border-white/10 bg-white/10 p-4 flex flex-col items-center gap-2">
      {hasDiscount && (
        <Badge
          text={`${offer.percentage}% OFF`}
          className="absolute -top-3 right-3 bg-badge text-foreground"
        />
      )}

      <span className="text-primary-foreground font-bold text-lg uppercase text-center">
        {offer.title ?? quantityLabel}
      </span>

      <span className="text-primary-foreground/60 text-xs uppercase">
        {quantityLabel}
      </span>

      <div className="flex items-baseline gap-2">
        {hasDiscount && (
          <span
            data-testid="upsell-offer-full-price"
            className="text-primary-foreground/40 text-sm line-through"
          >
            {formatCurrency(fullPrice)}
          </span>
        )}
        <span
          data-testid="upsell-offer-price"
          className="text-badge text-2xl font-semibold"
        >
          {formatCurrency(price)}
        </span>
      </div>

      <Button
        data-cy="post-game-upsell-accept"
        data-testid="post-game-upsell-accept"
        variant="tertiary"
        size="large"
        onClick={onAccept}
        className={cn(
          'w-full rounded-lg font-semibold h-11 text-sm mt-2',
          offer.highlight && 'animate-pulse duration-1000',
        )}
      >
        Comprar agora
      </Button>
    </div>
  );
};
